import { Table as BaseTable, Box, LoadingOverlay, Pagination, Select, Space, Text } from "@mantine/core";
import { DateValue } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import { CaretDownIcon, CaretUpDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import React, { JSX, ReactNode, useImperativeHandle } from "react";
import useSwr from "swr";
import { IFilter } from '../../models/Filter';
import { isArrivedOrder } from "../../utils/is-arrived-order";
import { EmptyState } from "../ui/empty-state";
import classes from "./table.module.css";
export * from "./row-action";
export * from "./table-action-button";
export * from "./table-actions";

export type ColumnType<T> = {
  title: ReactNode
  sorter?: boolean
  dataIndex?: string
  render: (record: T, index: number) => JSX.Element | string
  renderCheck?: (record: T, index: number) => JSX.Element | string
  align?: "left" | "right" | "center"
  width?: string | number
  titleAlign?: "left" | "right"
}

type Props = {
  name: string
  columns: ColumnType<any>[]
  thead?: JSX.Element
  filters?: {
    [key: string]: string | number | undefined | null | (string | number | null | DateValue)[]
  }
  pagination?: boolean
  dataSource?: any[]
  loadData?: (filter?: IFilter) => Promise<any>
  limit?: number
  onResult?: (data: any) => void
  onRowClick?: (record: any, index: number) => void
  /** Cell side padding; "sm" lets a wide list fit 1440px. */
  horizontalSpacing?: "sm" | "md"
  /** Replaces the default pagination row; receives the pager state. */
  footer?: (pager: TablePagerState) => JSX.Element
  /** Alternating row shade (default on); off inside a TableFrame, which uses row borders. */
  striped?: boolean
}

export type TablePagerState = {
  page: number
  limit: number
  total: number
  setPage: (page: number) => void
  setLimit: (limit: number) => void
}

export type ITableRef = {
  reload: () => void
  getSorted: () => string[]
}

export enum Sort {
  ASC = "ASC",
  DESC = "DESC",
}

export const Table = React.forwardRef(
  ({ name, columns, filters = {}, pagination = true, dataSource = [], loadData, limit = 10, thead, onResult, onRowClick, horizontalSpacing = "md", footer, striped = true }: Props, ref: React.Ref<ITableRef>) => {
    const scrollRef = React.useRef<HTMLDivElement>(null)
    const tableRef = React.useRef<HTMLTableElement>(null)
    const [offset, setOffset] = React.useState({ page: 1, limit: limit || 20 })
    const [sorted, setSorting] = React.useState<string[]>([])
    const [debouncedFilters] = useDebouncedValue(filters, 1000)
    const [debouncedSorted] = useDebouncedValue(sorted, 1000)

    // A new filter starts from page 1; staying on page 4 of a shorter result shows an empty table.
    const filterKey = JSON.stringify(debouncedFilters)
    React.useEffect(() => {
      setOffset((state) => (state.page === 1 ? state : { ...state, page: 1 }))
    }, [filterKey])

    const { data, mutate, isLoading } = useSwr(
      ["table", name, offset, debouncedFilters, debouncedSorted],
      async () => {
        if (loadData) {
          const data: any = {
            filter: debouncedFilters,
            offset,
          }
          if (debouncedSorted.length > 0) {
            data.order = debouncedSorted
          }
          return loadData(data)
        }
      },
      {
        fallbackData: { count: dataSource.length, rows: dataSource },
      },
    )

    const onSort = (dataIndex?: string) => {
      if (dataIndex) {
        setSorting((s) => [dataIndex, s[0] === dataIndex ? (s[1] === Sort.ASC ? Sort.DESC : Sort.ASC) : Sort.DESC])
      }
    }

    const onScroll = () => { }

    useImperativeHandle(ref, () => ({
      reload: mutate,
      getSorted: () => sorted,
    }))

    return (
      <Box className={classes.container}>
        <Box ref={scrollRef} onScroll={onScroll} className={classes.scroll}>
          <BaseTable ref={tableRef} horizontalSpacing={horizontalSpacing} verticalSpacing="sm" highlightOnHover striped={striped ? "even" : false}>
            <BaseTable.Thead>
              <BaseTable.Tr>
                {columns.map((column, index) => (
                  <Th
                    key={index}
                    column={column}
                    sorted={sorted[0] === column.dataIndex && sorted[1]}
                    onSort={() => onSort(column.dataIndex)}
                    align={column.titleAlign || "left"}>
                    {column.title}
                    {column.renderCheck && column.renderCheck(data, index)}
                  </Th>
                ))}
              </BaseTable.Tr>
            </BaseTable.Thead>
            <BaseTable.Tbody>
              {thead && thead}
              {data?.rows?.length > 0 && onResult && onResult(data)}
              {data?.rows?.length > 0 ? (
                data?.rows?.map((row: any, index: number) => {
                  return (
                    <BaseTable.Tr
                      key={row.id ?? index}
                      data-new={isArrivedOrder(row?.orderStatus) || undefined}
                      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                      style={onRowClick ? { cursor: "pointer" } : undefined}
                    >
                      {columns.map((column, index2) => {
                        return (
                          <BaseTable.Td key={`${index2}`} align={column.align || "left"}>
                            {column.render(row, index)}
                          </BaseTable.Td>
                        )
                      })}
                    </BaseTable.Tr>
                  )
                })
              ) : (
                <BaseTable.Tr key={"Noting"}>
                  <BaseTable.Td colSpan={columns.length}>
                    <EmptyState />
                  </BaseTable.Td>
                </BaseTable.Tr>
              )}
            </BaseTable.Tbody>
          </BaseTable>
          <Space h="md" />
          <LoadingOverlay visible={isLoading} />
        </Box>

        {pagination && footer && footer({
          page: offset.page,
          limit: offset.limit,
          total: data?.count ?? 0,
          setPage: (page) => setOffset((state) => ({ ...state, page })),
          setLimit: (next) => setOffset({ page: 1, limit: next }),
        })}
        {pagination && !footer && (
          <Box className={classes.pagination}>
            <Pagination
              total={Math.ceil(data.count / offset.limit)}
              value={offset.page}
              onChange={(page) => setOffset((state) => ({ ...state, page: page }))}
              siblings={1}
              boundaries={1}
              withControls={true}
              disabled={Math.ceil(data.count / offset.limit) <= 1}
              hideWithOnePage
            />
            {data.count > 0 && data && (
              <Select
                onChange={(e: string | null) => {
                  if (e !== null) {
                    setOffset((state) => ({ ...state, limit: parseInt(e, 10), page: 1 }))
                  }
                }}
                value={`${offset.limit}`}
                w={"100px"}
                size="sm"
                defaultValue={"10"}
                data={["5", "10", "20", "30", "50", "100", "200"]}
              />
            )}
          </Box>
        )}
      </Box>
    )
  },
)

interface ThProps {
  children: React.ReactNode
  column: ColumnType<any>
  sorted?: string | unknown
  align?: "left" | "right"
  onSort: () => void
}

const Th = ({ children, column, sorted, align, onSort }: ThProps) => {
  const Icon = sorted ? (sorted === Sort.ASC ? CaretUpIcon : CaretDownIcon) : CaretUpDownIcon

  if (!column.sorter)
    return (
      <BaseTable.Th style={{ width: column.width, textAlign: align }}>
        {/* Headers may wrap to two lines (canvas) so wide lists fit 1440px. */}
        <Text component="span" size="sm" fw={500}>{children}</Text>
      </BaseTable.Th>
    )

  return (
    <BaseTable.Th className={classes.control} style={{ width: column.width }}>
      <Box className={classes.header}>
        <Text component="span" className={classes.child} size="sm" fw={500}>{children}</Text>
        <Text component="span" className={classes.icon} onClick={() => onSort()}>
          <Icon size={14} className={sorted ? undefined : "sort-idle"} />
        </Text>
      </Box>
    </BaseTable.Th>
  )
}
